import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraBadge, SpecraButton, SpecraCheckbox, SpecraFormField, SpecraInput, SpecraOverlayDialog, SpecraOverlayDrawer, SpecraSelect, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useRef, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import { useProjectFiles } from '../../hooks/useProjectFiles';
import { parseDateInput, timestampToDateInput } from '../../lib/localCalendarDate';
import { CustomerContextRelease } from './CustomerContextRelease';
import { CustomerFollowUps } from './CustomerFollowUps';
import { CHANNELS, SENTIMENTS } from './constants';
import { DocumentsPanel } from './DocumentsPanel';
import { EventCombobox } from './EventCombobox';
import { useEvents } from './eventHooks';
import { useTouchpointProposals, useUpsertCustomerInteraction } from './hooks';
import { TouchpointParticipants } from './TouchpointParticipants';
const ids = Testids.growthShape01;
function saveErrorMessage(err) {
    switch(ConnectError.from(err).code){
        case Code.Unauthenticated:
            return 'Your session has expired. Copy your unsaved notes before signing in again, then reopen the touchpoint.';
        case Code.PermissionDenied:
            return 'You do not have permission to save this touchpoint. Copy your unsaved notes and ask your administrator for access.';
        case Code.InvalidArgument:
            return 'Some touchpoint or attendee details could not be accepted. Check the details you entered, reselect any linked People if needed, then save again. Draft fields may be left blank.';
        case Code.NotFound:
            return 'The Pursuit, touchpoint or a linked Person is no longer available. Copy your unsaved notes, then close and reopen the Pursuit to check access and reselect attendees before saving again.';
        case Code.Unavailable:
        case Code.DeadlineExceeded:
            return 'The connection was interrupted or the save timed out. Copy your unsaved notes, then reopen the Pursuit when connected and check whether the touchpoint saved before retrying, to avoid creating a duplicate note.';
        default:
            return 'We could not confirm the save. Copy your unsaved notes, then close and reopen the Pursuit to check whether the touchpoint saved before retrying, to avoid creating a duplicate note. If this continues, contact your administrator.';
    }
}
function editorValues(value) {
    return {
        id: value?.id ?? '',
        title: value?.title ?? '',
        summary: value?.summary ?? '',
        interactionDate: timestampToDateInput(value?.interactionDate),
        channel: value?.channel || 'meeting',
        eventId: value?.eventId ?? '',
        eventTitle: value?.eventTitle ?? '',
        stakeholder: value?.stakeholder ?? '',
        sentiment: value?.sentiment || 'unknown',
        followUps: value?.followUps ?? '',
        notes: value?.notes ?? '',
        inferredNeed: value?.inferredNeed ?? '',
        isDraft: value?.isDraft ?? !value,
        participants: value?.participants ?? []
    };
}
/** Mounted for one editing session; query refreshes never overwrite unsaved input. */ export function TouchpointSheet({ captureProjectId, pursuitName, zouProjectId, correlationId, interaction, onClose, initialParticipants, events }) {
    const recentEvents = useEvents({
        relevantToKind: 'PURSUIT',
        relevantToId: captureProjectId,
        limit: 1,
        enabled: !!events
    });
    const upsert = useUpsertCustomerInteraction(correlationId);
    const [saved, setSaved] = useState(()=>editorValues(interaction));
    const [values, setValues] = useState(()=>{
        const initial = editorValues(interaction);
        return interaction === null && initialParticipants?.length ? {
            ...initial,
            participants: initialParticipants
        } : initial;
    });
    const [error, setError] = useState('');
    const proposals = useTouchpointProposals(captureProjectId, saved.id);
    const files = useProjectFiles(zouProjectId ?? null);
    const [attachmentIds, setAttachmentIds] = useState([]);
    const [reviewError, setReviewError] = useState('');
    const [reviewBusy, setReviewBusy] = useState(false);
    const [needsReconciliation, setNeedsReconciliation] = useState(false);
    const reviewLock = useRef(false);
    const busy = upsert.isPending || reviewBusy;
    const [savedMessage, setSavedMessage] = useState('');
    const [confirmClose, setConfirmClose] = useState(false);
    const saving = useRef(false);
    const savedDate = useRef(interaction?.interactionDate);
    const dirty = JSON.stringify(values) !== JSON.stringify(saved);
    const blocker = useBlocker(dirty || busy);
    const persistedIds = new Set(saved.participants.map((p)=>p.id));
    const change = (key, value)=>{
        setValues((old)=>({
                ...old,
                [key]: value
            }));
        setSavedMessage('');
    };
    useEffect(()=>{
        const beforeUnload = (event)=>{
            if (!dirty && !saving.current && !reviewLock.current) return;
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', beforeUnload);
        return ()=>window.removeEventListener('beforeunload', beforeUnload);
    }, [
        dirty
    ]);
    const close = ()=>{
        if (saving.current || reviewLock.current) return;
        if (dirty) setConfirmClose(true);
        else onClose();
    };
    const save = async (isDraft)=>{
        if (saving.current || reviewLock.current || needsReconciliation) return;
        saving.current = true;
        setError('');
        setSavedMessage('');
        try {
            const response = await upsert.mutateAsync({
                ...values,
                captureProjectId,
                isDraft,
                interactionDate: values.interactionDate === saved.interactionDate ? savedDate.current?.toDate() ?? null : parseDateInput(values.interactionDate),
                participantUpserts: values.participants,
                participantRemovals: saved.participants.filter((p)=>!values.participants.some((row)=>row.id === p.id)).map((p)=>p.id)
            });
            if (!response.interaction) {
                setError('We could not confirm the save. Copy your unsaved notes, then close and reopen the Pursuit to check whether the touchpoint saved before retrying, to avoid creating a duplicate note.');
                return;
            }
            // Resolution may coalesce attendees. Only the returned IDs are valid for the next edit.
            const hydrated = editorValues(response.interaction);
            savedDate.current = response.interaction.interactionDate;
            setValues(hydrated);
            setSaved(hydrated);
            setSavedMessage(hydrated.isDraft ? 'Draft saved. You can reopen it or add files now.' : 'Finished note saved. You can add files now.');
        } catch (err) {
            setError(saveErrorMessage(err));
        } finally{
            saving.current = false;
        }
    };
    const runReview = async (action, kind = 'draft')=>{
        if (saving.current || reviewLock.current) return;
        if (kind !== 'refresh' && (dirty || needsReconciliation)) return;
        if (kind === 'refresh' && dirty && !needsReconciliation) return;
        reviewLock.current = true;
        setReviewBusy(true);
        setReviewError('');
        try {
            await action();
        } catch (err) {
            if (kind === 'decision') setNeedsReconciliation(true);
            setReviewError(kind === 'decision' || kind === 'refresh' && needsReconciliation ? 'The review decision could not be confirmed. Refresh saved values and history before saving or reviewing again. Your manual input is retained.' : ConnectError.from(err).code === Code.Aborted ? 'The saved details or source changed. Refresh saved values, inspect the sources, then draft again. Your manual input is retained.' : 'Suggestions could not be drafted. Your manual content is retained. Retry drafting or continue editing manually.');
        } finally{
            reviewLock.current = false;
            setReviewBusy(false);
        }
    };
    const decide = (proposal, decision, editedValue)=>runReview(async ()=>{
            const reviewed = await proposals.review.mutateAsync({
                proposalId: proposal.id,
                decision,
                editedValue
            });
            if (reviewed.state !== 'accepted') return;
            const target = reviewed.draft?.targetField;
            const key = target === 'summary' ? 'summary' : target === 'follow_ups' ? 'followUps' : target === 'inferred_need' ? 'inferredNeed' : undefined;
            if (!key || reviewed.appliedValue === undefined) throw new Error('Missing accepted value');
            const appliedValue = reviewed.appliedValue;
            // The decision writes exactly one target; never hydrate unrelated form fields here.
            setSaved((old)=>({
                    ...old,
                    [key]: appliedValue
                }));
            setValues((old)=>({
                    ...old,
                    [key]: appliedValue
                }));
            setSavedMessage('Suggestion accepted and saved.');
        }, 'decision');
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(SpecraOverlayDrawer, {
                open: true,
                onOpenChange: (open)=>{
                    if (!open) close();
                },
                title: values.id ? 'Edit Touchpoint' : 'Log Touchpoint',
                description: `Pursuit: ${pursuitName}`,
                testId: ids.editor,
                width: "lg",
                bodyProps: {
                    className: 'space-y-4'
                },
                footer: /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-wrap justify-end gap-2 p-4",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "ghost",
                            "data-testid": ids.close,
                            disabled: busy,
                            onClick: close,
                            children: "Close"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "secondary",
                            "data-testid": ids.saveDraft,
                            disabled: busy || needsReconciliation,
                            onClick: ()=>void save(true),
                            children: upsert.isPending ? 'Saving…' : 'Save draft'
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": ids.finish,
                            disabled: busy || needsReconciliation,
                            onClick: ()=>void save(false),
                            children: "Save finished note"
                        })
                    ]
                }),
                children: [
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        "data-testid": ids.context,
                        children: [
                            "Pursuit: ",
                            pursuitName
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        "data-testid": ids.status,
                        variant: values.isDraft ? 'warning' : 'success',
                        children: values.id ? values.isDraft ? 'Saved draft' : 'Finished note' : 'Unsaved draft'
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: "Manual notes stay in this Pursuit’s authorized context. Save a draft with any fields left blank, then add files. No transcript, AI review, Person or account is required."
                    }),
                    dirty && /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "warning",
                        children: "Unsaved changes"
                    }),
                    savedMessage && /*#__PURE__*/ _jsx(SpecraText, {
                        "aria-live": "polite",
                        children: savedMessage
                    }),
                    error && /*#__PURE__*/ _jsxs(SpecraText, {
                        role: "alert",
                        "data-testid": ids.saveError,
                        children: [
                            error,
                            " Your input is retained in this editor."
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("fieldset", {
                        disabled: busy,
                        className: "space-y-4",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Title",
                                htmlFor: "touchpoint-title",
                                children: /*#__PURE__*/ _jsx(SpecraInput, {
                                    id: "touchpoint-title",
                                    "data-testid": ids.title,
                                    value: values.title,
                                    onChange: (e)=>change('title', e.target.value)
                                })
                            }),
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Summary",
                                htmlFor: "touchpoint-summary",
                                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                    id: "touchpoint-summary",
                                    "data-testid": Testids.capture.interactionSummaryInput,
                                    value: values.summary,
                                    onChange: (e)=>change('summary', e.target.value),
                                    rows: 2
                                })
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "grid grid-cols-1 gap-3 sm:grid-cols-2",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraFormField, {
                                        label: "Date",
                                        htmlFor: "touchpoint-date",
                                        hint: "Optional for drafts. Finishing without a date uses today.",
                                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                                            id: "touchpoint-date",
                                            "data-testid": ids.date,
                                            type: "date",
                                            value: values.interactionDate,
                                            onChange: (e)=>change('interactionDate', e.target.value)
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraFormField, {
                                        label: "Type",
                                        htmlFor: "touchpoint-type",
                                        children: /*#__PURE__*/ _jsx(SpecraSelect, {
                                            id: "touchpoint-type",
                                            "data-testid": Testids.capture.interactionChannelSelect,
                                            value: values.channel,
                                            onChange: (e)=>change('channel', e.target.value),
                                            children: CHANNELS.map((c)=>/*#__PURE__*/ _jsx("option", {
                                                    value: c.value,
                                                    children: c.label
                                                }, c.value))
                                        })
                                    }),
                                    events && /*#__PURE__*/ _jsx(SpecraFormField, {
                                        label: "At an event",
                                        hint: "Industry day, conference or meeting this happened at. Other pursuits can link the same event.",
                                        children: /*#__PURE__*/ _jsx(EventCombobox, {
                                            canCreate: events.canWrite,
                                            opportunityId: events.opportunityId,
                                            description: values.channel === 'conference' && recentEvents.data?.[0] ? `Recent pursuit event: ${recentEvents.data[0].title}. Select it if this touchpoint happened there.` : undefined,
                                            onChange: (eventId, event)=>{
                                                setValues((old)=>({
                                                        ...old,
                                                        eventId,
                                                        eventTitle: event?.title ?? ''
                                                    }));
                                                setSavedMessage('');
                                            },
                                            selectedTitle: values.eventTitle,
                                            value: values.eventId
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraFormField, {
                                        label: "Sentiment",
                                        htmlFor: "touchpoint-sentiment",
                                        children: /*#__PURE__*/ _jsx(SpecraSelect, {
                                            id: "touchpoint-sentiment",
                                            value: values.sentiment,
                                            onChange: (e)=>change('sentiment', e.target.value),
                                            children: SENTIMENTS.map((s)=>/*#__PURE__*/ _jsx("option", {
                                                    value: s.value,
                                                    children: s.label
                                                }, s.value))
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraFormField, {
                                        label: "Additional attendee context",
                                        htmlFor: "touchpoint-stakeholder",
                                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                                            id: "touchpoint-stakeholder",
                                            value: values.stakeholder,
                                            onChange: (e)=>change('stakeholder', e.target.value)
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(TouchpointParticipants, {
                                participants: values.participants,
                                persistedIds: persistedIds,
                                captureProjectId: captureProjectId,
                                onChange: (participants)=>change('participants', participants),
                                disabled: busy
                            }, saved.participants.map((p)=>`${p.id}:${p.contactId}`).join(',')),
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Longer notes",
                                htmlFor: "touchpoint-notes",
                                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                    id: "touchpoint-notes",
                                    "data-testid": ids.notes,
                                    value: values.notes,
                                    onChange: (e)=>change('notes', e.target.value),
                                    rows: 8
                                })
                            }),
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Follow-up notes (unassigned)",
                                htmlFor: "touchpoint-followups",
                                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                    id: "touchpoint-followups",
                                    value: values.followUps,
                                    onChange: (e)=>change('followUps', e.target.value),
                                    rows: 3
                                })
                            })
                        ]
                    }),
                    values.inferredNeed && /*#__PURE__*/ _jsxs("div", {
                        "data-testid": Testids.growthShape02.inferredNeed,
                        children: [
                            /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: "warning",
                                children: "Inference"
                            }),
                            /*#__PURE__*/ _jsxs(SpecraText, {
                                children: [
                                    "Accepted inferred need: ",
                                    values.inferredNeed
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("section", {
                        className: "space-y-3",
                        "aria-label": "Touchpoint AI review",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraText, {
                                children: "Review suggestions"
                            }),
                            /*#__PURE__*/ _jsx(SpecraText, {
                                size: "meta",
                                tone: "muted",
                                children: "AI uses saved notes/transcript and selected Pursuit files. Review each interpretation before accepting. Follow-ups remain unassigned notes. Raw notes stay separate from suggestions."
                            }),
                            dirty && /*#__PURE__*/ _jsx(SpecraText, {
                                tone: "warning",
                                children: "Save your manual changes before drafting or reviewing."
                            }),
                            files.data?.files.map((file)=>/*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: `${file.name} · ${file.processingStatus}`,
                                    htmlFor: `touchpoint-attachment-${file.attachmentId}`,
                                    children: /*#__PURE__*/ _jsx(SpecraCheckbox, {
                                        id: `touchpoint-attachment-${file.attachmentId}`,
                                        "data-testid": Testids.growthShape02.attachment,
                                        disabled: busy || !attachmentIds.includes(file.attachmentId) && attachmentIds.length >= 7,
                                        checked: attachmentIds.includes(file.attachmentId),
                                        onCheckedChange: (checked)=>setAttachmentIds((old)=>checked === true ? [
                                                    ...old,
                                                    file.attachmentId
                                                ] : old.filter((id)=>id !== file.attachmentId))
                                    })
                                }, file.attachmentId)),
                            files.isError && /*#__PURE__*/ _jsx(SpecraText, {
                                tone: "warning",
                                children: "Pursuit files could not be loaded. Saved notes can still be used."
                            }),
                            !saved.id || !saved.notes.trim() && !attachmentIds.length ? /*#__PURE__*/ _jsx(SpecraText, {
                                "data-testid": Testids.growthShape02.empty,
                                children: "Save notes or a transcript first, or select an existing Pursuit file. You can log this Touchpoint manually."
                            }) : null,
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        "data-testid": Testids.growthShape02.draft,
                                        disabled: busy || dirty || needsReconciliation || !saved.id || !saved.notes.trim() && !attachmentIds.length,
                                        onClick: ()=>void runReview(async ()=>{
                                                await proposals.draft.mutateAsync(attachmentIds);
                                            }),
                                        children: "Draft / Review suggestions"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "secondary",
                                        "data-testid": Testids.growthShape02.refresh,
                                        disabled: busy || dirty && !needsReconciliation || !saved.id,
                                        onClick: ()=>void runReview(async ()=>{
                                                const response = await proposals.refresh();
                                                const fresh = response.interactions.find((row)=>row.id === saved.id);
                                                if (!fresh) throw new Error('Touchpoint unavailable');
                                                await proposals.history.refetch({
                                                    throwOnError: true
                                                });
                                                const hydrated = editorValues(fresh);
                                                // Adopt server changes only where the user has not made a manual edit.
                                                const reconciled = {
                                                    ...values
                                                };
                                                for (const key of Object.keys(hydrated)){
                                                    if (JSON.stringify(values[key]) === JSON.stringify(saved[key])) {
                                                        Object.assign(reconciled, {
                                                            [key]: hydrated[key]
                                                        });
                                                    }
                                                }
                                                savedDate.current = fresh.interactionDate;
                                                setSaved(hydrated);
                                                setValues(reconciled);
                                                setNeedsReconciliation(false);
                                            }, 'refresh'),
                                        children: "Refresh saved values and history"
                                    })
                                ]
                            }),
                            (reviewBusy || saved.id && proposals.history.isLoading) && /*#__PURE__*/ _jsx("output", {
                                className: "block text-sm",
                                "aria-live": "polite",
                                "data-testid": Testids.growthShape02.loading,
                                children: proposals.draft.isPending ? 'Reading saved sources and drafting suggestions… This can take a minute.' : 'Loading review decisions…'
                            }),
                            (reviewError || proposals.history.isError) && /*#__PURE__*/ _jsx(SpecraText, {
                                role: "alert",
                                "data-testid": Testids.growthShape02.error,
                                children: reviewError || 'Review history could not be loaded. Refresh to retry. Your manual content is retained.'
                            }),
                            !!proposals.history.data && /*#__PURE__*/ _jsxs("div", {
                                "data-testid": Testids.growthShape02.review,
                                className: "space-y-3",
                                children: [
                                    proposals.history.data.length === 0 && /*#__PURE__*/ _jsx(SpecraText, {
                                        children: "No suggestions yet. Draft from saved sources to begin."
                                    }),
                                    proposals.history.data.map((proposal)=>/*#__PURE__*/ _jsx(TouchpointProposalCard, {
                                            proposal: proposal,
                                            disabled: busy || dirty || needsReconciliation,
                                            sourceNames: Object.fromEntries((files.data?.files ?? []).map((file)=>[
                                                    file.attachmentId,
                                                    file.name
                                                ])),
                                            onDecide: decide
                                        }, proposal.id))
                                ]
                            })
                        ]
                    }),
                    saved.id && /*#__PURE__*/ _jsx(CustomerFollowUps, {
                        captureProjectId: captureProjectId,
                        sourceKind: "interaction",
                        sourceId: saved.id,
                        statement: saved.followUps
                    }),
                    saved.id && !saved.isDraft && /*#__PURE__*/ _jsx(CustomerContextRelease, {
                        captureProjectId: captureProjectId,
                        sourceKind: "interaction",
                        sourceId: saved.id
                    }),
                    values.id ? /*#__PURE__*/ _jsx(DocumentsPanel, {
                        entityType: "interaction",
                        entityId: values.id,
                        title: "Touchpoint files"
                    }) : /*#__PURE__*/ _jsx(SpecraText, {
                        tone: "muted",
                        children: "Save a draft first to add files. The editor will stay open."
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
                open: confirmClose || blocker.state === 'blocked',
                onOpenChange: (open)=>{
                    if (!open) {
                        setConfirmClose(false);
                        if (blocker.state === 'blocked') blocker.reset();
                    }
                },
                title: upsert.isPending ? 'Touchpoint is saving' : 'Discard unsaved changes?',
                description: "Keep editing to save your note before leaving.",
                footer: /*#__PURE__*/ _jsxs("div", {
                    className: "flex justify-end gap-2 p-4",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "secondary",
                            "data-testid": ids.keepEditing,
                            onClick: ()=>{
                                setConfirmClose(false);
                                if (blocker.state === 'blocked') blocker.reset();
                            },
                            children: "Keep editing"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": ids.discard,
                            disabled: busy,
                            onClick: ()=>{
                                if (saving.current || reviewLock.current) return;
                                if (blocker.state === 'blocked') blocker.proceed();
                                else onClose();
                            },
                            children: "Discard changes"
                        })
                    ]
                }),
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    children: upsert.isPending ? 'Wait for the save to finish before leaving.' : 'Changes since your last save will be lost.'
                })
            })
        ]
    });
}
function TouchpointProposalCard({ proposal, disabled, sourceNames, onDecide }) {
    const [editing, setEditing] = useState(false);
    const [editedValue, setEditedValue] = useState(proposal.draft?.proposedValue ?? '');
    const draft = proposal.draft;
    if (!draft) return null;
    const ids = Testids.growthShape02;
    const target = draft.targetField === 'summary' ? 'Summary' : draft.targetField === 'follow_ups' ? 'Follow-up notes (unassigned)' : 'Inferred need';
    return /*#__PURE__*/ _jsxs("article", {
        className: "space-y-2 rounded-md border p-3",
        "data-testid": ids.proposal,
        "data-proposal-id": proposal.id,
        "data-target": draft.targetField,
        "data-state": proposal.state,
        children: [
            /*#__PURE__*/ _jsx(SpecraBadge, {
                "data-testid": ids.label,
                variant: draft.classification === 'FACT' ? 'neutral' : 'warning',
                children: {
                    FACT: 'Documented customer statement',
                    INTERNAL_ASSESSMENT: 'Internal assessment',
                    INFERENCE: 'Inference',
                    UNVERIFIED_REPORT: 'Unverified report',
                    UNKNOWN: 'Unknown'
                }[draft.classification] ?? 'Unknown'
            }),
            /*#__PURE__*/ _jsx(SpecraText, {
                children: target
            }),
            /*#__PURE__*/ _jsx(SpecraText, {
                className: "whitespace-pre-wrap",
                children: draft.proposedValue
            }),
            /*#__PURE__*/ _jsxs(SpecraText, {
                size: "meta",
                children: [
                    "Rationale: ",
                    draft.rationale
                ]
            }),
            draft.uncertainty && /*#__PURE__*/ _jsxs(SpecraText, {
                size: "meta",
                children: [
                    "Uncertainty / missing evidence: ",
                    draft.uncertainty
                ]
            }),
            draft.contradictions && /*#__PURE__*/ _jsxs(SpecraText, {
                size: "meta",
                children: [
                    "Contradictions: ",
                    draft.contradictions
                ]
            }),
            draft.sources.map((source, index)=>/*#__PURE__*/ _jsxs("div", {
                    "data-testid": ids.source,
                    className: "rounded border p-2 text-sm",
                    children: [
                        /*#__PURE__*/ _jsxs(SpecraText, {
                            size: "meta",
                            children: [
                                source.kind === 'notes' ? 'Saved notes / transcript' : sourceNames[source.ref] || 'Pursuit attachment',
                                ' ',
                                "· ",
                                source.sourceDate
                            ]
                        }),
                        /*#__PURE__*/ _jsx("blockquote", {
                            className: "whitespace-pre-wrap",
                            children: source.excerpt || 'No direct quotation; see the raw source snapshot.'
                        }),
                        /*#__PURE__*/ _jsxs("details", {
                            children: [
                                /*#__PURE__*/ _jsx("summary", {
                                    children: "Raw source text"
                                }),
                                /*#__PURE__*/ _jsx("pre", {
                                    className: "whitespace-pre-wrap",
                                    children: source.text
                                })
                            ]
                        })
                    ]
                }, `${source.ref}:${index}`)),
            /*#__PURE__*/ _jsxs(SpecraText, {
                "data-testid": ids.state,
                children: [
                    proposal.state === 'pending' ? 'Pending review' : proposal.state === 'accepted' ? 'Accepted' : 'Rejected',
                    proposal.reviewerId && ` · Reviewer: ${proposal.reviewerId}`,
                    proposal.reviewedAt && ` · ${proposal.reviewedAt.toDate().toLocaleString()}`
                ]
            }),
            proposal.state === 'accepted' && /*#__PURE__*/ _jsxs(SpecraText, {
                className: "whitespace-pre-wrap",
                children: [
                    "Saved value: ",
                    proposal.appliedValue
                ]
            }),
            proposal.state === 'pending' && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    editing && /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: `Edit ${target}`,
                        htmlFor: `proposal-${proposal.id}`,
                        children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                            id: `proposal-${proposal.id}`,
                            "data-testid": ids.editedValue,
                            value: editedValue,
                            disabled: disabled,
                            onChange: (event)=>setEditedValue(event.target.value),
                            rows: 3
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": ids.accept,
                                disabled: disabled || editing && !editedValue.trim(),
                                onClick: ()=>void onDecide(proposal, 'accepted', editing ? editedValue : undefined),
                                children: editing ? 'Accept edited value' : 'Accept'
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "secondary",
                                "data-testid": editing ? ids.cancelEdit : ids.edit,
                                disabled: disabled,
                                onClick: ()=>{
                                    setEditing(!editing);
                                    setEditedValue(draft.proposedValue);
                                },
                                children: editing ? 'Cancel edit' : 'Edit then accept'
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "ghost",
                                "data-testid": ids.reject,
                                disabled: disabled,
                                onClick: ()=>void onDecide(proposal, 'rejected'),
                                children: "Reject"
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
